/**
 * Dialog wrapper for product feedback so the dashboard stays focused on CVs.
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../contexts/LanguageContext';
import FeedbackForm from './FeedbackForm';
import './FeedbackModal.css';

const FeedbackModal = ({ open, onClose, page = '', variant = 'default' }) => {
  const { t } = useLanguage();
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const timer = window.setTimeout(() => closeRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="feedback-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-form-title"
    >
      <button
        type="button"
        className="feedback-modal__backdrop"
        aria-label={t('feedback.close')}
        onClick={onClose}
      />
      <div className="feedback-modal__panel">
        <button
          type="button"
          ref={closeRef}
          className="feedback-modal__close"
          aria-label={t('feedback.close')}
          onClick={onClose}
        >
          ×
        </button>
        <FeedbackForm page={page} compact plain variant={variant} />
      </div>
    </div>
  );
};

FeedbackModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  page: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'afterDownload']),
};

export default FeedbackModal;
